import { DEFAULT_TUNING_RANGE_PERCENT } from './constants';
import { OptimizationSettings } from './types';

/** What the optimizer does with one reference flavor. */
export type FlavorMode = 'free' | 'lock' | 'remove' | 'tune';

export interface FlavorToken {
  flavor: string;
  label: string;
  mode: FlavorMode;
  /** The target product's decomp share of this flavor, in percent; absent when it has none. */
  sharePercent?: number;
  /** In the parent report's reference flavors, so the optimizer can act on it. */
  inReport: boolean;
}

export interface LexiconFlavor {
  flavor: string;
}

type FlavorLists = Pick<OptimizationSettings, 'refFlavorsToLock' | 'refFlavorsToRemove' | 'refFlavorsToTune'>;

/**
 * A lexicon name as the optimizer's reference flavor key, e.g. "Crème fraîche"
 * → `creme_fraiche`, "Buddha's Hand" → `buddhas_hand`. Mirrors the cleaning the
 * R pipeline applies; matches 98% of lexicon names to keys in real reports.
 */
export const flavorKey = (name: string) =>
  name
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/['\u2019]/g, '')
    .replace(/[^a-z0-9&]+/g, '_')
    .replace(/^_+|_+$/g, '');

/** R's per-GGVar `generic_*` aggregates are not real flavors and are never optimized. */
const isGeneric = (flavor: string) => flavor.startsWith('generic_');

export const flavorLabel = (flavor: string) => flavor.replace(/_/g, ' ');

export const tokenMode = (opt: FlavorLists, flavor: string): FlavorMode => {
  if (opt.refFlavorsToLock.includes(flavor)) return 'lock';
  if (opt.refFlavorsToRemove.includes(flavor)) return 'remove';
  if (opt.refFlavorsToTune.some((t) => t.flavor === flavor)) return 'tune';
  return 'free';
};

/** Puts each flavor in at most one of lock / remove / tune, so the lists can never conflict. */
export const withTokenModes = (
  opt: FlavorLists,
  flavors: string[],
  mode: FlavorMode,
): FlavorLists => {
  const target = new Set(flavors);
  const keep = (f: string) => !target.has(f);
  const tune = opt.refFlavorsToTune.filter((t) => keep(t.flavor));
  const [min, max] = DEFAULT_TUNING_RANGE_PERCENT;
  return {
    refFlavorsToLock: [...opt.refFlavorsToLock.filter(keep), ...(mode === 'lock' ? flavors : [])],
    refFlavorsToRemove: [...opt.refFlavorsToRemove.filter(keep), ...(mode === 'remove' ? flavors : [])],
    refFlavorsToTune:
      mode === 'tune'
        ? [
            ...tune,
            ...flavors.map(
              (flavor) => opt.refFlavorsToTune.find((t) => t.flavor === flavor) ?? { flavor, min, max },
            ),
          ]
        : tune,
  };
};

interface PoolInput {
  opt: FlavorLists;
  /** The parent report's reference flavors. */
  reportFlavors: string[];
  /** Target product decomp share per flavor, as a 0–1 fraction. */
  shares: ReadonlyMap<string, number>;
  lexicon: LexiconFlavor[];
  /** Offer every report and lexicon flavor, not just the product's decomp flavors. */
  offerAll?: boolean;
}

const constrainedFlavors = (opt: FlavorLists) => [
  ...opt.refFlavorsToLock,
  ...opt.refFlavorsToRemove,
  ...opt.refFlavorsToTune.map((t) => t.flavor),
];

/**
 * Every flavor that can be constrained: constrained ones (so a cloned choice
 * never disappears), the product's decomp flavors and, with `offerAll`, the
 * report's and the lexicon's. Sorted by the product's share, then A–Z.
 * `reportFlavors` is always needed to tell which flavors are in the report.
 */
export const buildTokenPool = ({ opt, reportFlavors, shares, lexicon, offerAll = true }: PoolInput): FlavorToken[] => {
  // Keys can differ only in case between sources (e.g. `Citrus` vs the lexicon's `citrus`);
  // the first spelling seen wins, so report and constrained spellings beat the lexicon's.
  const canonical = new Map<string, string>();
  const add = (flavor: string) => {
    const key = flavor.toLowerCase();
    if (!canonical.has(key)) canonical.set(key, flavor);
  };
  const inReport = new Set(reportFlavors.map((f) => f.toLowerCase()));
  constrainedFlavors(opt).forEach(add);
  shares.forEach((share, flavor) => share > 0 && !isGeneric(flavor) && add(flavor));
  if (offerAll) {
    reportFlavors.filter((f) => !isGeneric(f)).forEach(add);
    lexicon.forEach((l) => add(l.flavor));
  }

  const tokens = Array.from(canonical.entries(), ([key, flavor]): FlavorToken => {
    const share = shares.get(flavor);
    return {
      flavor,
      // One spelling per flavor in every view, so names never change as the search widens.
      label: flavorLabel(flavor),
      mode: tokenMode(opt, flavor),
      sharePercent: share && share > 0 ? Math.round(share * 1000) / 10 : undefined,
      inReport: inReport.has(key),
    };
  });

  return tokens.sort(
    (a, b) =>
      Number(a.sharePercent === undefined) - Number(b.sharePercent === undefined) ||
      (b.sharePercent ?? 0) - (a.sharePercent ?? 0) ||
      a.label.localeCompare(b.label),
  );
};
